import { http } from '@/lib/axios';
import { unwrap, toApiError } from '@/lib/unwrap';
import type { ApiEnvelope } from '@/types/api';
import type { EligibleDiscount, PromoCandidate } from '@/types/coupon';
import { effectiveSourceChannel } from '@/lib/salesChannel';

export interface EligibleCart {
  items: Array<{ productId: string; quantity: number; price?: number }>;
  totalAmount: number;
}

/**
 * Promo-scheme context (v.1.0.22). Scheme-gated discounts (GROUP / IDENTITAS /
 * PAYMENT / channel) only surface as eligible when the matching context is
 * supplied — the backend runs the same sub-validators the sale path does.
 */
export interface EligibleContext {
  groupType?: string | null;
  sourceChannel?: string | null;
  paymentGateway?: string | null;
  paymentMethodId?: string | null;
  identity?: {
    idCardRegion?: string;
    birthMonth?: number | null;
    ktpVerified?: boolean;
  } | null;
  /**
   * Document-OCR scan context (v.1.0.24). The preview runs the same document
   * sub-validator the sale does; `fields` are the server-extracted values from
   * POST /document/scan (round-tripped for the preview only — the sale re-reads
   * them from the trusted token).
   */
  document?: {
    documentType: string;
    fields: Record<string, string>;
  } | null;
  /** The visit day (YYYY-MM-DD), so a visit-date-limited promo is judged for it. */
  visitDate?: string | null;
}

/** A preview is judged for the channel the sale will carry (see buildSalePayload). */
function withChannel(context?: EligibleContext): EligibleContext {
  return { ...(context ?? {}), sourceChannel: effectiveSourceChannel(context?.sourceChannel) };
}

/** POST /discount/eligible — auto-eligible discounts for a cart. Fail-open. */
export async function getEligibleDiscounts(
  cart: EligibleCart,
  context?: EligibleContext,
): Promise<EligibleDiscount[]> {
  try {
    const res = await http.post<ApiEnvelope<EligibleDiscount[]>>('/discount/eligible', {
      cart,
      context: withChannel(context),
    });
    const data = unwrap(res.data);
    return Array.isArray(data) ? data : [];
  } catch (error) {
    const err = toApiError(error);
    // Optional surface — never block checkout on a discount lookup.
    if (err.isNetwork || err.code >= 500) return [];
    if (err.code === 404) return [];
    return [];
  }
}

/**
 * POST /discount/candidates — EVERY showInUI promo for the picker, eligible or
 * not, each annotated with the context it requires + eligibility + savings.
 * Backs the per-promo context reveal. Fail-open (never blocks checkout).
 */
export async function getPromoCandidates(
  cart: EligibleCart,
  context?: EligibleContext,
): Promise<PromoCandidate[]> {
  try {
    const res = await http.post<ApiEnvelope<PromoCandidate[]>>('/discount/candidates', {
      cart,
      context: withChannel(context),
    });
    const data = unwrap(res.data);
    return Array.isArray(data) ? data : [];
  } catch {
    return [];
  }
}
